import { Label, Select, SelectOption } from '@pay-com/components'
import {
  Controller,
  FieldPath,
  FieldValues,
  useFormContext,
  useWatch
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ICustomFieldComponentProps } from 'types'
import { CountryAlpha2 } from 'generated/graphql'
import { stateOptionsByCountry } from 'consts'
import { ControlledInput } from 'components/ControlledFields/ControlledFormElements/ControlledInput/ControlledInput'
import { CountrySingleSelect } from 'components/CountrySingleSelect'
import { getErrorMessage } from 'utils/get-error-message'

type AddressFieldsProps = ICustomFieldComponentProps<
  FieldValues,
  FieldPath<FieldValues>,
  {
    withCountry?: boolean
    country?: CountryAlpha2
    placeholders?: {
      addressLine?: string
      addressLine2?: string
      city?: string
      country?: string
      state?: string
      zip?: string
    }
  }
>

export const AddressFields = ({
  name,
  label,
  withCountry,
  country,
  placeholders
}: AddressFieldsProps) => {
  const base = name.split('.').slice(0, -1).join('.')
  const { control } = useFormContext()
  const { t } = useTranslation('common')
  const { t: tErrors } = useTranslation('errors')

  const selectedCountry = useWatch({
    control,
    name: `${base}.countryAlpha2`
  }) as CountryAlpha2 | undefined

  const resolvedCountry = selectedCountry ?? country
  const stateOptions = resolvedCountry
    ? stateOptionsByCountry[resolvedCountry]
    : undefined

  return (
    <div className="flex flex-col gap-1">
      {label && <Label label={label} />}
      <div className="flex flex-col gap-2">
        {withCountry && (
          <CountrySingleSelect
            name={`${base}.countryAlpha2`}
            placeholder={placeholders?.country ?? t('common.address.country')}
          />
        )}
        <ControlledInput
          name={`${base}.addressLine`}
          placeholder={
            placeholders?.addressLine ?? t('common.address.addressLine')
          }
        />
        <ControlledInput
          name={`${base}.addressLine2`}
          placeholder={
            placeholders?.addressLine2 ?? t('common.address.addressLine2')
          }
        />
        <ControlledInput
          name={`${base}.city`}
          placeholder={placeholders?.city ?? t('common.address.city')}
        />
        {stateOptions && (
          <Controller
            name={`${base}.state`}
            control={control}
            render={({ field, fieldState }) => {
              const currentValue =
                stateOptions.find(option => option.value === field.value) ??
                null

              return (
                <Select
                  value={currentValue}
                  options={stateOptions}
                  onChange={(option: SelectOption | null) =>
                    field.onChange(option?.value ?? null)
                  }
                  onBlur={field.onBlur}
                  placeholder={placeholders?.state ?? t('common.address.state')}
                  error={Boolean(fieldState.error)}
                  errorMessage={getErrorMessage(
                    tErrors,
                    fieldState.error?.message
                  )}
                  size="small"
                  searchByLabelAndByValue
                />
              )
            }}
          />
        )}
        <ControlledInput
          name={`${base}.zip`}
          placeholder={placeholders?.zip ?? t('common.address.zip')}
        />
      </div>
    </div>
  )
}
